import React, { useEffect, useRef, useState } from "react";
import { playResult } from "@/lib/feedbackSounds";
import { SoundToggle } from "@/components/SoundToggle";
import { Timer, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BEGINNER_TERMS } from "@/lib/theory";

const SECONDS = 60;
const shuffle = (a) => { const b = [...a]; for (let i = b.length - 1; i > 0; i -= 1) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j], b[i]]; } return b; };

const makeQuestion = (deck) => {
  const card = deck[Math.floor(Math.random() * deck.length)];
  const pool = [...new Map([...deck, ...BEGINNER_TERMS].map((t) => [t.meaning, t])).values()].filter((t) => t.meaning !== card.meaning);
  return { card, options: shuffle([card.meaning, ...shuffle(pool).slice(0, 3).map((t) => t.meaning)]) };
};

const Board = ({ board }) => (
  <div className="mt-5 rounded-2xl border border-border bg-card p-4" data-testid="terms-quiz-board">
    <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Trophy size={13} /> This week's top 10 · resets every Monday</p>
    {!board?.entries?.length ? <p className="mt-2 text-sm text-muted-foreground" data-testid="terms-quiz-board-empty">No scores yet this week — be the first!</p> : (
      <ol className="mt-2 space-y-1">
        {board.entries.map((e) => (
          <li key={e.rank} className={`flex justify-between rounded-lg px-2 py-1 text-sm ${e.is_me ? "bg-primary/15 font-semibold" : ""}`} data-testid={`terms-quiz-row-${e.rank}`}>
            <span>{e.rank}. {e.name}</span><span className="font-mono">{e.score}</span>
          </li>
        ))}
      </ol>
    )}
  </div>
);

export const TermsQuiz = ({ deck }) => {
  const { user } = useAuth();
  const [phase, setPhase] = useState("idle");
  const [left, setLeft] = useState(SECONDS);
  const [score, setScore] = useState(0);
  const [q, setQ] = useState(null);
  const [flash, setFlash] = useState(null);
  const flashT = useRef(null);
  const [board, setBoard] = useState(null);
  const scoreRef = useRef(0);
  useEffect(() => { api.get("/terms-quiz/leaderboard").then(({ data }) => setBoard(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (phase !== "play") return undefined;
    const t = setInterval(() => setLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [phase]);
  useEffect(() => {
    if (phase !== "play" || left > 0) return;
    setPhase("done");
    if (user) api.post("/terms-quiz/score", { score: scoreRef.current }).then(({ data }) => setBoard(data)).catch(() => {});
  }, [left, phase, user]);
  const start = () => { scoreRef.current = 0; setScore(0); setLeft(SECONDS); setQ(makeQuestion(deck)); setPhase("play"); };
  const answer = (opt) => {
    const ok = opt === q.card.meaning;
    playResult(ok);
    setFlash(ok ? "right" : "wrong");
    clearTimeout(flashT.current);
    flashT.current = setTimeout(() => setFlash(null), 450);
    scoreRef.current = Math.max(0, scoreRef.current + (opt === q.card.meaning ? 1 : -1));
    setScore(scoreRef.current);
    setQ(makeQuestion(deck));
  };
  if (deck.length < 2) return null;
  return (
    <section className="mt-10" data-testid="terms-quiz">
      <h2 className="font-serif text-2xl">Terms quiz</h2>
      <p className="mt-1 text-sm text-muted-foreground">How many terms can you match in {SECONDS} seconds? +1 for a right answer, −1 for a wrong one. Your best score each week goes on the board.</p>
      <div className={`mt-4 rounded-3xl border-2 bg-card p-5 transition-colors ${flash === "right" ? "border-emerald-500" : flash === "wrong" ? "border-red-500" : "border-border"}`} data-testid="terms-quiz-card">
        {phase === "play" && q ? (
          <>
            <div className="flex items-center justify-between font-mono text-xs">
              <span className="flex items-center gap-1 text-primary" data-testid="terms-quiz-timer"><Timer size={13} /> {left}s</span>
              <SoundToggle testId="terms-quiz-sound-toggle" />
              <span data-testid="terms-quiz-score">Score: {score}</span>
            </div>
            <p className="mt-4 text-center font-serif text-4xl italic text-primary" data-testid="terms-quiz-sign">{q.card.sign}</p>
            {q.card.term !== q.card.sign && <p className="mt-1 text-center text-sm text-muted-foreground">{q.card.term}</p>}
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {q.options.map((o, i) => <Button key={o} variant="outline" onClick={() => answer(o)} className="h-auto whitespace-normal rounded-2xl border-border py-3" data-testid={`terms-quiz-option-${i}`}>{o}</Button>)}
            </div>
          </>
        ) : (
          <div className="text-center">
            {phase === "done" && <p className="font-serif text-2xl" data-testid="terms-quiz-result">You scored {score}!</p>}
            {phase === "done" && !user && <p className="mt-1 text-xs text-muted-foreground">Log in to save your score.</p>}
            <Button onClick={start} className="mt-3 rounded-full bg-primary text-primary-foreground" data-testid="terms-quiz-start">{phase === "done" ? "Play again" : "Start the 60-second quiz"}</Button>
            <div className="mt-3"><SoundToggle testId="terms-quiz-start-sound-toggle" /></div>
          </div>
        )}
      </div>
      <Board board={board} />
    </section>
  );
};
